/**
 * Owns the vendored POC panel (window.Rhs4) for the popover (plan 15.2 option B, F3).
 *
 * The panel renders into ONE detached element created here and kept for the life of the popover;
 * BrainPanelHost attaches it while the popover is open, so minimise/restore keeps the tab and the
 * DOM (FloatingPanel unmounts its children in the bubble). Rhs4.mount runs once: a ref guards the
 * React StrictMode double effect, and Rhs4.mount on the same element is itself idempotent.
 * Every tse:diag_state re-renders the panel; a new diagnosis_id resets it to the Diagnosis tab (16.4).
 */
import { useEffect, useMemo, useRef } from "react";

import { vendoredPanel } from "../../vendor/brain";
import { toPanelState } from "./rhs4Adapter";

export const DIAGNOSIS_TAB = "diagnosis";
// The POC callbacks the panel fires; each is forwarded as (name, args) to onPanelCallback.
const PANEL_CALLBACKS = [
  "onOverride", "onUndoOverride", "onFocusLod", "onTeach", "onLearning", "onSettings", "onHotline",
  "onTabChange", "onPendingQuestion",
];

const createHost = () => (typeof document === "undefined" ? null : document.createElement("div"));

/**
 * @param {{diagState: object|null, bundle: object|null, devMode: boolean, initialTab?: string,
 *          onPanelCallback: (name: string, args: any[]) => void}} params
 * @returns {{hostEl: HTMLElement|null, focusTab: (tab: string, section?: string) => void}}
 */
export default function useVendoredPanel({ diagState, bundle, devMode, initialTab, onPanelCallback }) {
  const hostRef = useRef(null);
  if (!hostRef.current) hostRef.current = createHost();
  const mountedRef = useRef(false);
  const onCallbackRef = useRef(onPanelCallback);
  onCallbackRef.current = onPanelCallback;
  const lastDiagnosisRef = useRef(null);

  const callbacks = useMemo(() => Object.fromEntries(PANEL_CALLBACKS.map((name) => [
    name, (...args) => onCallbackRef.current?.(name, args),
  ])), []);

  useEffect(() => {
    const { Rhs4 } = vendoredPanel();
    if (!Rhs4 || !hostRef.current || mountedRef.current) return;
    mountedRef.current = true;
    Rhs4.mount(hostRef.current, callbacks);
    if (initialTab) Rhs4.focus(initialTab);
    // Mounted once per popover; initialTab is only read on that first mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callbacks]);

  const panelState = useMemo(
    () => toPanelState(diagState, bundle, { devMode, pcApi: vendoredPanel().V4Pc }),
    [diagState, bundle, devMode],
  );

  useEffect(() => {
    const { Rhs4 } = vendoredPanel();
    if (!Rhs4 || !mountedRef.current) return;
    const diagnosisId = diagState?.diagnosis_id || null;
    const changed = lastDiagnosisRef.current !== diagnosisId;
    if (changed && (lastDiagnosisRef.current !== null || diagnosisId === null)) Rhs4.reset();
    Rhs4.render(panelState, callbacks);
    if (changed && lastDiagnosisRef.current !== null && diagnosisId !== null) Rhs4.focus(DIAGNOSIS_TAB);
    lastDiagnosisRef.current = diagnosisId;
  }, [panelState, diagState, callbacks]);

  const focusTab = useMemo(() => (tab, section) => {
    const { Rhs4 } = vendoredPanel();
    if (Rhs4 && mountedRef.current) Rhs4.focus(tab, section);
  }, []);

  return { hostEl: hostRef.current, focusTab };
}
